/* eslint-disable @next/next/no-img-element */
import React from 'react'

import { AA_TEXT, ensureContrast } from '@/lib/color/contrast'
import Image from '@/components/Image'
import {
  EMAIL_FONT,
  GLYPH,
  GLYPH_ALT,
  GLYPH_GUTTER,
  PAPER,
  SignatureModel,
  TYPE,
  absoluteAsset,
  glyphMetrics,
  tint,
} from '@/components/EmailSignaturePreview/signature'

/**
 * Email-safe building blocks shared by all six signature templates.
 *
 * The contract for everything in this file, because a signature is pasted into renderers
 * that are two decades behind the browser:
 *
 * - **Tables for layout.** No flex, no grid, no `gap`, no class names, no CSS variables, no
 *   `<style>` block — Gmail strips one out of pasted markup, which also rules out media
 *   queries, so responsiveness has to come from the layout itself.
 * - **No `margin`, anywhere.** Outlook renders through Word, which drops margins on tables
 *   and is unreliable on block elements. Every gap in a signature is cell padding instead,
 *   which is what `Stack` is for.
 * - **Hybrid widths.** Each frame carries both a `width` attribute in px (Word obeys the
 *   attribute and ignores `max-width`) and `width:100%; max-width:Npx` in CSS (every modern
 *   client obeys that and ignores the attribute). The block is therefore fixed and correct
 *   in Outlook and fluid down to a phone everywhere else.
 * - **Nothing inherits.** Font family, size, line height and colour are restated on every
 *   text node; Outlook and Gmail both drop inherited typography at unpredictable depths.
 *
 * The same components render the on-screen preview with `interactive` on, which is the only
 * thing that adds class names (hover affordances) and swaps the raw `<img>` for the app's
 * optimised image. Preview and export therefore cannot drift apart: they are one layout.
 */

export type PartProps = {
  model: SignatureModel
  interactive?: boolean
}

/** Class names are preview-only — the exported markup must carry none. */
const ui = (interactive: boolean | undefined, className: string) =>
  interactive ? className : undefined

const LINK_RESET: React.CSSProperties = { textDecoration: 'none' }

/** Long values (an address, a share URL) wrap instead of forcing the block wider. */
const WRAP: React.CSSProperties = { wordBreak: 'break-word', overflowWrap: 'break-word' }

/** Kills the half-em Word inserts around every table it meets. */
const RESET_TABLE = {
  borderCollapse: 'collapse',
  msoTableLspace: '0pt',
  msoTableRspace: '0pt',
} as React.CSSProperties

/**
 * Word needs an exact line height or it re-flows the block to its own leading. The key is
 * cast because React's CSSProperties has no room for vendor-prefixed mail-client hints.
 */
const MSO_EXACT = { msoLineHeightRule: 'exactly' } as React.CSSProperties

/** Middle dot separating parts of a run. Non-breaking, so a line never wraps onto a dot. */
const Dot = () => <span style={{ color: PAPER.divider }}>&nbsp;·&nbsp;</span>

/* -------------------------------------------------------------------------- */
/* Structure                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Outermost wrapper every template starts from. `width` is the desktop cap; below it the
 * block is fluid, so the same markup reads correctly in a 560px compose window and on a
 * 320px phone without a single media query.
 */
export const SignatureFrame = ({
  children,
  width = 520,
  align = 'left',
}: React.PropsWithChildren<{ width?: number; align?: 'left' | 'center' }>) => (
  <table
    cellPadding="0"
    cellSpacing="0"
    border={0}
    role="presentation"
    width={width}
    style={{
      ...RESET_TABLE,
      width: '100%',
      maxWidth: `${width}px`,
      fontFamily: EMAIL_FONT,
      color: PAPER.ink,
      textAlign: align,
    }}
  >
    <tbody>{children}</tbody>
  </table>
)

/**
 * Stacks blocks with an even rhythm. Children become table rows whose gap is the next row's
 * top padding, so the spacing survives Word; `null` children (a card with no address, no
 * socials, no notes) collapse without leaving a gap behind.
 */
export const Stack = ({
  gap = 0,
  align = 'left',
  children,
}: React.PropsWithChildren<{ gap?: number; align?: 'left' | 'center' }>) => {
  const items = React.Children.toArray(children).filter(Boolean)
  if (!items.length) return null

  return (
    <table
      cellPadding="0"
      cellSpacing="0"
      border={0}
      role="presentation"
      style={{ ...RESET_TABLE, width: '100%' }}
    >
      <tbody>
        {items.map((child, index) => (
          <tr key={index}>
            <td
              valign="top"
              style={{
                verticalAlign: 'top',
                textAlign: align,
                paddingTop: index === 0 ? undefined : `${gap}px`,
              }}
            >
              {child}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** A hairline that survives Outlook: a filled table cell, never a `border`. */
export const Rule = ({
  color = PAPER.hairline,
  height = 1,
  radius = 0,
}: {
  color?: string
  height?: number
  radius?: number
}) => (
  <table
    cellPadding="0"
    cellSpacing="0"
    border={0}
    role="presentation"
    style={{ ...RESET_TABLE, width: '100%' }}
  >
    <tbody>
      <tr>
        <td
          style={{
            height: `${height}px`,
            lineHeight: `${height}px`,
            fontSize: '0px',
            backgroundColor: color,
            borderRadius: radius ? `${radius}px` : undefined,
          }}
        >
          &nbsp;
        </td>
      </tr>
    </tbody>
  </table>
)

/**
 * The hairline between a mark and the details — the corporate signature's signature. The
 * rule is a cell with a background, so the row stretches it to exactly the height of the
 * content beside it; a nested table with `height:100%` would not grow in any mail client.
 */
export const VerticalRule = ({
  color = PAPER.hairline,
  width = 1,
  gutter = 16,
}: {
  color?: string
  width?: number
  gutter?: number
}) => (
  <>
    <td
      width={width}
      style={{
        width: `${width}px`,
        backgroundColor: color,
        borderRadius: width > 1 ? `${width}px` : undefined,
        fontSize: '0px',
        lineHeight: '0px',
      }}
    >
      &nbsp;
    </td>
    <td width={gutter} style={{ width: `${gutter}px`, fontSize: '0px', lineHeight: '0px' }}>
      &nbsp;
    </td>
  </>
)

/**
 * A fixed-width leader beside a fluid column — the only side-by-side shape that survives a
 * phone, since the left side never has to give ground and the right simply narrows.
 */
export const MediaRow = ({
  lead,
  leadWidth,
  gutter = 14,
  align = 'middle',
  children,
}: React.PropsWithChildren<{
  lead: React.ReactNode
  leadWidth: number
  gutter?: number
  align?: 'top' | 'middle'
}>) => (
  <table
    cellPadding="0"
    cellSpacing="0"
    border={0}
    role="presentation"
    style={{ ...RESET_TABLE, width: '100%' }}
  >
    <tbody>
      <tr>
        <td
          valign={align}
          width={leadWidth + gutter}
          style={{
            width: `${leadWidth + gutter}px`,
            paddingRight: `${gutter}px`,
            verticalAlign: align,
          }}
        >
          {lead}
        </td>
        <td valign={align} style={{ verticalAlign: align }}>
          {children}
        </td>
      </tr>
    </tbody>
  </table>
)

/* -------------------------------------------------------------------------- */
/* Logo                                                                        */
/* -------------------------------------------------------------------------- */

type LogoProps = PartProps & {
  size?: number
  /** `tile` = rounded square (the default mark); `round` = circular avatar. */
  variant?: 'tile' | 'round'
  align?: 'left' | 'center'
}

/**
 * The card's mark. With an image it is the image, cropped to the shape; without one it is a
 * brand chip carrying the initials, so the block never opens on an empty square.
 */
export const SignatureLogo = ({
  model,
  interactive,
  size = 56,
  variant = 'tile',
  align = 'left',
}: LogoProps) => {
  const hasImage = Boolean(model.logoSrc)
  const radius = variant === 'round' ? size : 10

  return (
    <table
      cellPadding="0"
      cellSpacing="0"
      border={0}
      role="presentation"
      width={size}
      align={align === 'center' ? 'center' : undefined}
      style={{
        ...RESET_TABLE,
        width: `${size}px`,
        borderRadius: `${radius}px`,
        backgroundColor: hasImage ? PAPER.white : tint(model.brand, 0.88),
      }}
    >
      <tbody>
        <tr>
          <td
            align="center"
            valign="middle"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              textAlign: 'center',
              verticalAlign: 'middle',
              borderRadius: `${radius}px`,
              overflow: 'hidden',
            }}
          >
            {hasImage ? (
              React.createElement(interactive ? Image : 'img', {
                src: model.logoSrc,
                alt: model.name,
                width: size,
                height: size,
                style: {
                  width: `${size}px`,
                  height: `${size}px`,
                  objectFit: 'cover',
                  borderRadius: `${radius}px`,
                  display: 'block',
                  border: 0,
                },
              })
            ) : (
              <span
                style={{
                  fontFamily: EMAIL_FONT,
                  fontSize: `${Math.round(size / 2.6)}px`,
                  lineHeight: `${size}px`,
                  fontWeight: 600,
                  letterSpacing: '0.2px',
                  // The chip is the brand tinted almost to white, so the initials need their
                  // own correction — `brandInk` is measured against paper, not against this.
                  color: ensureContrast(model.brand, tint(model.brand, 0.88), AA_TEXT),
                }}
              >
                {model.initials}
              </span>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  )
}

/* -------------------------------------------------------------------------- */
/* Name + role                                                                 */
/* -------------------------------------------------------------------------- */

export const SignatureName = ({
  model,
  scale = 'name',
  font = EMAIL_FONT,
  align = 'left',
}: PartProps & {
  scale?: 'name' | 'nameLarge'
  font?: string
  align?: 'left' | 'center'
}) => {
  const { size, line, weight, tracking } = TYPE[scale]

  return model.name ? (
    <div
      style={{
        fontFamily: font,
        fontSize: `${size}px`,
        lineHeight: `${line}px`,
        ...MSO_EXACT,
        fontWeight: weight,
        letterSpacing: tracking,
        color: PAPER.ink,
        textAlign: align,
        ...WRAP,
      }}
    >
      {model.name}
    </div>
  ) : null
}

/**
 * `Head of Partnerships · Stripe` — one quiet line. Every part reads at the same weight; the
 * old layout emphasised the first one, which fought the name directly above it.
 */
export const SignatureRole = ({
  model,
  align = 'left',
  font = EMAIL_FONT,
}: PartProps & { align?: 'left' | 'center'; font?: string }) =>
  model.meta.length ? (
    <div
      style={{
        fontFamily: font,
        fontSize: `${TYPE.role.size}px`,
        lineHeight: `${TYPE.role.line}px`,
        ...MSO_EXACT,
        color: PAPER.inkMuted,
        textAlign: align,
        ...WRAP,
      }}
    >
      {model.meta.map((part, index) => (
        <React.Fragment key={part}>
          {index > 0 && <Dot />}
          {part}
        </React.Fragment>
      ))}
    </div>
  ) : null

/**
 * The enterprise hierarchy: the title on its own line, the company beneath it. The company
 * carries a little more weight than the title because it is the thing a stranger scans for.
 */
export const SignatureTitle = ({
  model,
  align = 'left',
  font = EMAIL_FONT,
}: PartProps & { align?: 'left' | 'center'; font?: string }) => {
  const company = model.meta.filter((part) => part !== model.jobPosition)
  if (!model.jobPosition && !company.length) return null

  const line: React.CSSProperties = {
    fontFamily: font,
    fontSize: `${TYPE.role.size}px`,
    lineHeight: `${TYPE.role.line}px`,
    ...MSO_EXACT,
    textAlign: align,
    ...WRAP,
  }

  return (
    <div>
      {model.jobPosition && (
        <div style={{ ...line, color: PAPER.inkMuted }}>{model.jobPosition}</div>
      )}
      {company.length > 0 && (
        <div style={{ ...line, color: PAPER.ink, fontWeight: 500 }}>
          {company.map((part, index) => (
            <React.Fragment key={part}>
              {index > 0 && <Dot />}
              {index > 0 ? (
                <span style={{ fontWeight: 400, color: PAPER.inkMuted }}>{part}</span>
              ) : (
                part
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Name over title over company — the identity block, identical in all six templates.
 *
 * The templates are arrangements of one design, not six designs: they move this block, the
 * mark and the rule around, and change nothing about how any of them is set. Keeping the
 * block in one component is what makes that true rather than aspirational.
 */
export const SignatureIdentity = ({
  model,
  align = 'left',
}: PartProps & { align?: 'left' | 'center' }) => (
  <Stack gap={3} align={align}>
    <SignatureName model={model} align={align} />
    <SignatureTitle model={model} align={align} />
  </Stack>
)

/* -------------------------------------------------------------------------- */
/* Contact rows                                                                */
/* -------------------------------------------------------------------------- */

export type ContactEntry = {
  key: string
  glyph: string
  text: React.ReactNode
  href?: string
  color: string
  weight?: number
  kind: 'phone' | 'email' | 'website' | 'address' | 'link' | 'file'
  /** `primary` = the reachable-now values; `secondary` = address, links and files. */
  group: 'primary' | 'secondary'
}

export const primaryOf = (entries: ContactEntry[]) => entries.filter((e) => e.group === 'primary')
export const secondaryOf = (entries: ContactEntry[]) =>
  entries.filter((e) => e.group === 'secondary')

/**
 * Every contact value the card carries, in one order that all six templates share. Nothing is
 * ever dropped: a template chooses how to *set* the list, not which of it to keep.
 */
export const collectContactEntries = (model: SignatureModel): ContactEntry[] => {
  const entries: ContactEntry[] = []
  const aside = (label: string) => <span style={{ color: PAPER.inkFaint }}> ({label})</span>

  model.phones.forEach(({ key, value, label, href }) => {
    entries.push({
      key: `phone-${key}`,
      glyph: GLYPH.phone,
      kind: 'phone',
      href,
      color: PAPER.ink,
      group: 'primary',
      text: (
        <>
          {value}
          {label && aside(label)}
        </>
      ),
    })
  })

  model.emails.forEach(({ key, value, label, href }) => {
    entries.push({
      key: `email-${key}`,
      glyph: GLYPH.email,
      kind: 'email',
      href,
      color: model.brandInk,
      weight: 500,
      group: 'primary',
      text: (
        <>
          {value}
          {label && aside(label)}
        </>
      ),
    })
  })

  if (model.website) {
    entries.push({
      key: 'website',
      glyph: GLYPH.website,
      kind: 'website',
      href: model.website,
      color: PAPER.ink,
      text: model.websiteLabel,
      group: 'primary',
    })
  }

  model.addresses.forEach(({ key, value, label }) => {
    entries.push({
      key,
      glyph: GLYPH.address,
      kind: 'address',
      color: PAPER.inkMuted,
      group: 'secondary',
      text: (
        <>
          {label && <span style={{ color: PAPER.inkFaint }}>{label}: </span>}
          {value}
        </>
      ),
    })
  })

  model.links.forEach(({ key, label, url }) => {
    entries.push({
      key: `link-${key}`,
      glyph: GLYPH.link,
      kind: 'link',
      href: url,
      color: model.brandInk,
      weight: 500,
      text: label,
      group: 'secondary',
    })
  })

  model.files.forEach(({ id, name, url }) => {
    entries.push({
      key: `file-${id}`,
      glyph: GLYPH.file,
      kind: 'file',
      href: url,
      color: model.brandInk,
      weight: 500,
      text: name,
      group: 'secondary',
    })
  })

  return entries
}

/**
 * The glyph-led detail list. One column, always: a two-column grid needs a media query to
 * survive a phone, and there is no media query to be had in pasted markup.
 */
export const ContactRows = ({
  entries,
  interactive,
  assetBaseUrl,
  font = EMAIL_FONT,
  gap = 5,
}: {
  entries: ContactEntry[]
  interactive?: boolean
  /** Absolute origin so the glyphs resolve once the markup is pasted into a mail client. */
  assetBaseUrl?: string
  font?: string
  gap?: number
}) => {
  if (!entries.length) return null

  const { size, line } = TYPE.row
  const { box, columnWidth, offset } = glyphMetrics(14, line)

  return (
    <table
      cellPadding="0"
      cellSpacing="0"
      border={0}
      role="presentation"
      style={{ ...RESET_TABLE, width: '100%' }}
    >
      <tbody>
        {entries.map(({ key, glyph, kind, text, href, color, weight }, index) => (
          <tr key={key}>
            <td
              valign="top"
              width={columnWidth}
              style={{
                width: `${columnWidth}px`,
                paddingRight: `${GLYPH_GUTTER}px`,
                paddingTop: `${index === 0 ? offset : offset + gap}px`,
                verticalAlign: 'top',
                fontSize: '0px',
                lineHeight: `${box}px`,
              }}
            >
              <img
                src={absoluteAsset(glyph, assetBaseUrl)}
                width={box}
                height={box}
                alt={GLYPH_ALT[kind] ?? ''}
                style={{
                  display: 'block',
                  width: `${box}px`,
                  height: `${box}px`,
                  border: 0,
                  outline: 'none',
                  textDecoration: 'none',
                }}
              />
            </td>
            <td
              valign="top"
              style={{
                paddingTop: index === 0 ? undefined : `${gap}px`,
                verticalAlign: 'top',
                fontFamily: font,
                fontSize: `${size}px`,
                lineHeight: `${line}px`,
                ...MSO_EXACT,
                color,
                fontWeight: weight ?? 400,
                ...WRAP,
              }}
            >
              {href ? (
                <a
                  href={href}
                  style={{ ...LINK_RESET, color, fontWeight: weight ?? 400 }}
                  className={ui(interactive, 'hover:underline')}
                >
                  {text}
                </a>
              ) : (
                text
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** The same values as one wrapping run separated by middle dots — for the glyph-less styles. */
export const ContactInline = ({
  entries,
  interactive,
  font = EMAIL_FONT,
  scale = 'row',
  align = 'left',
}: {
  entries: ContactEntry[]
  interactive?: boolean
  font?: string
  scale?: 'row' | 'small'
  align?: 'left' | 'center'
}) => {
  if (!entries.length) return null

  const { size, line } = TYPE[scale]

  return (
    <div
      style={{
        fontFamily: font,
        fontSize: `${size}px`,
        lineHeight: `${line}px`,
        ...MSO_EXACT,
        color: PAPER.inkMuted,
        textAlign: align,
        ...WRAP,
      }}
    >
      {entries.map(({ key, kind, text, href, color, weight }, index) => {
        // A number, an address and a URL are single unbreakable units, so a line wraps at a
        // dot rather than mid-value. A street address is not — holding that on one line
        // forces the whole block wider than the phone it is being read on.
        const atomic = kind !== 'address'
        const style: React.CSSProperties = {
          color,
          fontWeight: weight ?? 400,
          whiteSpace: atomic ? 'nowrap' : undefined,
        }

        return (
          <React.Fragment key={key}>
            {index > 0 && <Dot />}
            {href ? (
              <a
                href={href}
                style={{ ...LINK_RESET, ...style }}
                className={ui(interactive, 'hover:underline')}
              >
                {text}
              </a>
            ) : (
              <span style={style}>{text}</span>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Socials + save contact                                                      */
/* -------------------------------------------------------------------------- */

/** The "Save contact" pill: a real download glyph, never a typed arrow character. */
export const SaveContactLink = ({
  model,
  interactive,
  height = 26,
}: PartProps & { height?: number }) => {
  const glyph = 13

  return (
    <a
      href={model.downloadUrl}
      style={{
        ...LINK_RESET,
        display: 'inline-block',
        borderRadius: `${height}px`,
        border: `1px solid ${PAPER.hairline}`,
        backgroundColor: PAPER.white,
      }}
      className={ui(interactive, 'transition-colors duration-[120ms] hover:border-[#DFE2E8]')}
    >
      <table cellPadding="0" cellSpacing="0" border={0} role="presentation" style={RESET_TABLE}>
        <tbody>
          <tr>
            <td
              valign="middle"
              style={{
                height: `${height}px`,
                paddingLeft: '10px',
                paddingRight: '6px',
                verticalAlign: 'middle',
                fontSize: '0px',
                lineHeight: `${height}px`,
              }}
            >
              <img
                src={absoluteAsset(GLYPH.download, model.assetBaseUrl)}
                alt=""
                width={glyph}
                height={glyph}
                style={{
                  display: 'inline-block',
                  width: `${glyph}px`,
                  height: `${glyph}px`,
                  verticalAlign: 'middle',
                  border: 0,
                }}
              />
            </td>
            <td
              valign="middle"
              style={{
                height: `${height}px`,
                paddingRight: '11px',
                verticalAlign: 'middle',
                fontFamily: EMAIL_FONT,
                fontSize: '12px',
                lineHeight: `${height}px`,
                ...MSO_EXACT,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                color: PAPER.inkMuted,
              }}
            >
              Save contact
            </td>
          </tr>
        </tbody>
      </table>
    </a>
  )
}

/**
 * Social marks and the "Save contact" pill on one row.
 *
 * The chips are brand-filled because the shipped social marks are white artwork — tinting
 * them to sit on a grey chip would need a CSS filter, and Outlook drops filters, which would
 * leave an empty circle. Each mark carries its network name as alt text, so a client that
 * blocks images shows the name rather than a gap.
 */
export const SignatureActions = ({
  model,
  interactive,
  align = 'left',
  chipSize = 26,
  /** `text` drops the chips for a quiet run of named links — used by Minimal. */
  variant = 'chip',
  font = EMAIL_FONT,
}: PartProps & {
  align?: 'left' | 'center'
  chipSize?: number
  variant?: 'chip' | 'text'
  font?: string
}) => {
  if (!model.socials.length && !model.downloadUrl) return null

  const mark = Math.round(chipSize * 0.54)

  if (variant === 'text') {
    return (
      <div
        style={{
          fontFamily: font,
          fontSize: `${TYPE.small.size}px`,
          lineHeight: `${TYPE.small.line}px`,
          ...MSO_EXACT,
          color: PAPER.inkSubtle,
          textAlign: align,
          ...WRAP,
        }}
      >
        {model.socials.map(({ type, url, name }, index) => (
          <React.Fragment key={type}>
            {index > 0 && <Dot />}
            <a
              href={url}
              style={{ ...LINK_RESET, color: PAPER.inkSubtle }}
              className={ui(interactive, 'hover:underline')}
            >
              {name}
            </a>
          </React.Fragment>
        ))}
        {model.downloadUrl && (
          <>
            {model.socials.length > 0 && <Dot />}
            <a
              href={model.downloadUrl}
              style={{ ...LINK_RESET, color: model.brandInk, fontWeight: 500 }}
              className={ui(interactive, 'hover:underline')}
            >
              Save contact
            </a>
          </>
        )}
      </div>
    )
  }

  return (
    <table
      cellPadding="0"
      cellSpacing="0"
      border={0}
      role="presentation"
      align={align === 'center' ? 'center' : undefined}
      style={{ ...RESET_TABLE, display: align === 'center' ? 'inline-table' : undefined }}
    >
      <tbody>
        <tr>
          {model.socials.map(({ type, url, name, svgImg }, index) => (
            <td
              key={type}
              valign="middle"
              style={{
                paddingRight: index === model.socials.length - 1 ? 0 : '6px',
                verticalAlign: 'middle',
              }}
            >
              {/* The chip is its own single-cell table, not a cell of the row: a `td` in the
                  row would be stretched to the height of the tallest sibling — the Save
                  contact pill — and the circle would come out an oval. `align`/`valign` on
                  the inner cell is the one centring every mail client agrees on. */}
              <table
                cellPadding="0"
                cellSpacing="0"
                border={0}
                role="presentation"
                width={chipSize}
                style={{
                  ...RESET_TABLE,
                  width: `${chipSize}px`,
                  borderRadius: `${chipSize}px`,
                  // The marks are white artwork, so the chip has to stay dark enough to hold them.
                  backgroundColor: model.brandEdge,
                }}
              >
                <tbody>
                  <tr>
                    <td
                      width={chipSize}
                      height={chipSize}
                      align="center"
                      valign="middle"
                      style={{
                        width: `${chipSize}px`,
                        height: `${chipSize}px`,
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        fontSize: '0px',
                        lineHeight: '0px',
                      }}
                    >
                      <a
                        href={url}
                        title={name}
                        style={{ ...LINK_RESET, display: 'inline-block', fontSize: '0px' }}
                        className={ui(
                          interactive,
                          'transition-opacity duration-[120ms] hover:opacity-80'
                        )}
                      >
                        <img
                          src={absoluteAsset(svgImg, model.assetBaseUrl)}
                          alt={name}
                          width={mark}
                          height={mark}
                          style={{
                            display: 'block',
                            width: `${mark}px`,
                            height: `${mark}px`,
                            border: 0,
                          }}
                        />
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          ))}

          {model.downloadUrl && (
            <td
              valign="middle"
              style={{
                paddingLeft: model.socials.length ? '10px' : 0,
                verticalAlign: 'middle',
                // Zeroed so the pill's inline-block has no baseline strut under it; with one
                // it hangs a few pixels above the chips it is supposed to sit level with.
                fontSize: '0px',
                lineHeight: '0px',
              }}
            >
              <SaveContactLink model={model} interactive={interactive} height={chipSize} />
            </td>
          )}
        </tr>
      </tbody>
    </table>
  )
}

/* -------------------------------------------------------------------------- */
/* Notes                                                                       */
/* -------------------------------------------------------------------------- */

/** The card's own blurb, set quietly under a hairline. Upright — the old italic shouted. */
export const SignatureNotes = ({
  model,
  align = 'left',
  font = EMAIL_FONT,
}: PartProps & { align?: 'left' | 'center'; font?: string }) =>
  model.notes ? (
    <Stack gap={10}>
      <Rule color={PAPER.hairlineSoft} />
      <div
        style={{
          fontFamily: font,
          fontSize: `${TYPE.small.size}px`,
          lineHeight: `${TYPE.small.line}px`,
          ...MSO_EXACT,
          color: PAPER.inkSubtle,
          whiteSpace: 'pre-wrap',
          textAlign: align,
          ...WRAP,
        }}
      >
        {model.notes}
      </div>
    </Stack>
  ) : null
